import { useCallback, useEffect, useState } from 'react';
import { clientInformApi, getBuildingUnitId } from '../api/clientInformApi';

/**
 * Resolve, per building unit, who would actually receive the notification.
 *
 * The backend resolver behind /partners/inform-client/recipients is the same one
 * the preview and the send use, so the recipients column always matches what a
 * send would do: a unit's own operation contacts win, and only a unit without any
 * falls back to its building's contacts.
 *
 * @param {Array} selectedUnits - rows selected in the scheduler table
 * @param {boolean} enabled - typically the modal's `visible` flag
 * @returns {{ contactsByUnitId: Map<number, Array>, loading: boolean, reload: Function }}
 */
const useClientInformRecipients = (selectedUnits, enabled) => {
  const [contactsByUnitId, setContactsByUnitId] = useState(new Map());
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    const buildingUnitIds = [...new Set(
      (selectedUnits || []).map(getBuildingUnitId).filter(Boolean)
    )];

    if (!buildingUnitIds.length) {
      setContactsByUnitId(new Map());
      return;
    }

    setLoading(true);

    try {
      const response = await clientInformApi.recipients({ building_units: buildingUnitIds });
      const byUnit = response?.data?.by_unit || {};

      setContactsByUnitId(new Map(
        Object.entries(byUnit).map(([unitId, contacts]) => [
          Number(unitId),
          Array.isArray(contacts) ? contacts : [],
        ])
      ));
    } catch (error) {
      console.error('Failed to load inform recipients:', error);
      setContactsByUnitId(new Map());
    } finally {
      setLoading(false);
    }
  }, [selectedUnits]);

  useEffect(() => {
    if (enabled && selectedUnits?.length) {
      reload();
    }
    if (!enabled) {
      setContactsByUnitId(new Map());
      setLoading(false);
    }
  }, [enabled, selectedUnits, reload]);

  return { contactsByUnitId, loading, reload };
};

export default useClientInformRecipients;
